import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useLazyQuery, useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup, Input, Card, PageHeader, Button } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";

import DatePicker from "components/cck/DatePicker";
import Helmet from "components/helmet/Helmet";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import ConnectingVesselSelect from "view/connectingVessels/components/selects/ConnectingVesselSelect";
import LocationSelect from "view/locations/components/selects/LocationSelect";

import { Language } from "imports/Utils";

import ConnectingVisitStatusSelect from "./components/selects/ConnectingVisitStatusSelect";
import { connectingVisitSchema, ConnectingVisitSchemaInput, ConnectingVisitSchemaOutput } from "./utils/connectingvisit.schema";

import { ConnectingVisitStatus } from "__generated__/enums";
import { SaveConnectingVisitDocument } from "__generated__/view/connectingVisits/graphql/connectingVisit.mutations.generated";
import { GetConnectingVisitDocument } from "__generated__/view/connectingVisits/graphql/connectingVisit.queries.generated";

const ConnectingVisitCreateEdit = () => {

	const navigate = useNavigate();
	const { id } = useParams();

	const [ getConnectingVisit ] = useLazyQuery(GetConnectingVisitDocument);
	const [ saveConnectingVisit, { loading } ] = useMutation(SaveConnectingVisitDocument);

	const { control, handleSubmit, reset } = useForm<ConnectingVisitSchemaInput, unknown, ConnectingVisitSchemaOutput>({
		resolver: zodResolver(connectingVisitSchema),
		defaultValues: {
			id: null,
			vessel: null,
			port: null,
			callReferenceNumber: "",
			status: ConnectingVisitStatus.PreArrived,
			eta: null,
			ata: null,
			etd: null,
			atd: null,
			cargoOpening: null,
			cargoClosing: null,
			cargoAvailableStart: null,
			cargoAvailableEnd: null,
		},
	});

	const save = (data: ConnectingVisitSchemaOutput) => {
		saveConnectingVisit({
			variables: {
				input: data,
			},
		}).then(() => {
			navigate("/view/connectingvisits");
		});
	};

	useEffect(() => {
		if (id) {
			getConnectingVisit({
				variables: {
					id: Number(id),
				},
			}).then(({ data }) => {
				reset({
					id: data?.connectingVisit?.id ?? null,
					vessel: data?.connectingVisit?.vessel ?? null,
					port: data?.connectingVisit?.port ?? null,
					callReferenceNumber: data?.connectingVisit?.callReferenceNumber ?? "",
					status: data?.connectingVisit?.status ?? ConnectingVisitStatus.PreArrived,
					eta: data?.connectingVisit?.eta ?? null,
					ata: data?.connectingVisit?.ata ?? null,
					etd: data?.connectingVisit?.etd ?? null,
					atd: data?.connectingVisit?.atd ?? null,
					cargoOpening: data?.connectingVisit?.cargoOpening ?? null,
					cargoClosing: data?.connectingVisit?.cargoClosing ?? null,
					cargoAvailableStart: data?.connectingVisit?.cargoAvailableStart ?? null,
					cargoAvailableEnd: data?.connectingVisit?.cargoAvailableEnd ?? null,
				});
			});
		}
	}, []);

	return (
		<form onSubmit={handleSubmit(save)}>
			<PageHeader>
				<PageHeader.Title>
					{Language("CONNECTING_VISITS")}
					<Helmet title={intl.formatMessage({ id: "CONNECTING_VISITS" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate("/view/connectingvisits")} variant="outlined">
						{Language("CANCEL")}
					</Button>

					<Button loading={loading} type="submit">
						{Language("SAVE")}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<Grid spacing={3} container>
				<Grid size={6}>
					<Card>
						<FormGroup label={intl.formatMessage({ id: "CONNECTING_VESSEL" })} labelPlacement="left-center" overflow="hidden">
							<Controller
								name='vessel'
								control={control}
								render={({ field }) => <ConnectingVesselSelect {...field} /> } />
						</FormGroup>

						<FormGroup
							label={Language("LOCATION")}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name="port"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<LocationSelect {...field} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup
							label={intl.formatMessage({ id: "CONNECTING_ORDER_CALL_REFERENCE_NUMBER" })}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name='callReferenceNumber'
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Input {...field} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup
							label={intl.formatMessage({ id: "STATUS" })}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name='status'
								control={control}
								render={({ field }) => <ConnectingVisitStatusSelect {...field} clearable={false} /> } />
						</FormGroup>

						<FormGroup
							label={intl.formatMessage({ id: "CONNECTING_VISITS_ETA" })}
							labelPlacement="left-center"
							overflow="hidden">
							<Controller
								name="eta"
								control={control}
								render={({ field }) => <DatePicker {...field} showTimeInput />} />
						</FormGroup>

						<FormGroup
							label={intl.formatMessage({ id: "CONNECTING_VISITS_ATA" })}
							labelPlacement="left-center"
							overflow="hidden">
							<Controller
								name="ata"
								control={control}
								render={({ field }) => <DatePicker {...field} showTimeInput />} />
						</FormGroup>

						<FormGroup
							label={intl.formatMessage({ id: "CONNECTING_VISITS_ETD" })}
							labelPlacement="left-center"
							overflow="hidden">
							<Controller
								name="etd"
								control={control}
								render={({ field }) => <DatePicker {...field} showTimeInput />} />
						</FormGroup>

						<FormGroup
							label={intl.formatMessage({ id: "CONNECTING_VISITS_ATD" })}
							labelPlacement="left-center"
							overflow="hidden">
							<Controller
								name="atd"
								control={control}
								render={({ field }) => <DatePicker {...field} showTimeInput />} />
						</FormGroup>

						<FormGroup
							label={intl.formatMessage({ id: "CONNECTING_ORDER_CARGO_OPENING" })}
							labelPlacement="left-center"
							overflow="hidden">
							<Controller
								name="cargoOpening"
								control={control}
								render={({ field }) => <DatePicker {...field} showTimeInput />} />
						</FormGroup>

						<FormGroup
							label={intl.formatMessage({ id: "CONNECTING_ORDER_CARGO_CLOSING" })}
							labelPlacement="left-center"
							overflow="hidden">
							<Controller
								name="cargoClosing"
								control={control}
								render={({ field }) => <DatePicker {...field} showTimeInput />} />
						</FormGroup>

						<FormGroup
							label={intl.formatMessage({ id: "CONNECTING_ORDER_CARGO_AVAILABLE_START" })}
							labelPlacement="left-center"
							overflow="hidden">
							<Controller
								name="cargoAvailableStart"
								control={control}
								render={({ field }) => <DatePicker {...field} showTimeInput />} />
						</FormGroup>

						<FormGroup
							label={intl.formatMessage({ id: "CONNECTING_ORDER_CARGO_AVAILABLE_END" })}
							labelPlacement="left-center"
							overflow="hidden">
							<Controller
								name="cargoAvailableEnd"
								control={control}
								render={({ field }) => <DatePicker {...field} showTimeInput />} />
						</FormGroup>
					</Card>
				</Grid>
			</Grid>
		</form>
	);
};

export default ConnectingVisitCreateEdit;

